import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { X, Download, AlertTriangle, CheckCircle2 } from "lucide-react";
import { personFromDocument, validateSes, buildSesXml, searchMunicipios, MAX_GUESTS } from "../utils/ses";

// Parte de viajeros para SES.Hospedajes (España): XML para «Alta masiva» del portal del
// Ministerio del Interior, con los DNI y pasaportes seleccionados del historial
// (30-9-2026). Las tablas (municipios INE, países, pagos, parentescos) se cargan al abrir.

const fieldsOf = (entry) => entry.fields || entry.extractedJson?.fields || [];
const pad = (n) => String(n).padStart(2, "0");
const localDateTime = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
const localDate = (d) => localDateTime(d).slice(0, 10);
const STORE_KEY = "ses_codigo_establecimiento";

function readStore() {
  try {
    return window.localStorage.getItem(STORE_KEY) || "";
  } catch (_) {
    return "";
  }
}

const inputCls = "mt-1 w-full rounded-lg border border-gray-200 px-2 py-1.5 text-sm";
const badCls = "border-amber-400 bg-amber-50";

function Label({ children, bad, hint }) {
  return (
    <span className={`text-xs font-semibold ${bad ? "text-amber-800" : "text-gray-600"}`}>
      {children}
      {hint && <span className="ml-1 font-normal text-gray-400">{hint}</span>}
    </span>
  );
}

function MunicipioPicker({ list, value, onPick, bad, placeholder }) {
  const [q, setQ] = useState(value?.name || "");
  const [open, setOpen] = useState(false);
  const results = useMemo(() => searchMunicipios(list, q), [list, q]);
  return (
    <div className="relative">
      <input
        className={`${inputCls} ${bad ? badCls : ""}`}
        value={q}
        placeholder={placeholder}
        onChange={(e) => {
          setQ(e.target.value);
          setOpen(true);
          if (value?.code) onPick(null);
        }}
        onFocus={() => setOpen(true)}
      />
      {open && results.length > 0 && (
        <ul className="absolute z-10 mt-1 max-h-48 w-full overflow-auto rounded-lg border border-gray-200 bg-white text-sm shadow-lg">
          {results.map(([code, name]) => (
            <li key={code}>
              <button
                type="button"
                className="block w-full px-3 py-1.5 text-left hover:bg-primary-50"
                onClick={() => {
                  onPick({ code, name });
                  setQ(name);
                  setOpen(false);
                }}
              >
                {name} <span className="text-xs text-gray-400">{code}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function AddressForm({ value, onChange, errors, t, countries, municipios }) {
  const set = (k, v) => onChange({ ...value, [k]: v });
  const has = (k) => errors.includes(k);
  const spain = value.pais === "ESP";
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <label className="sm:col-span-2">
        <Label bad={has("direccion")}>{t("ses.address")}</Label>
        <input className={`${inputCls} ${has("direccion") ? badCls : ""}`} value={value.direccion || ""} onChange={(e) => set("direccion", e.target.value)} maxLength={100} />
      </label>
      <label className="sm:col-span-2">
        <Label hint={t("ses.optional")}>{t("ses.address2")}</Label>
        <input className={inputCls} value={value.direccionComplementaria || ""} onChange={(e) => set("direccionComplementaria", e.target.value)} maxLength={100} />
      </label>
      <label>
        <Label bad={has("pais")}>{t("ses.country")}</Label>
        <select className={`${inputCls} ${has("pais") ? badCls : ""}`} value={value.pais || ""} onChange={(e) => onChange({ ...value, pais: e.target.value, codigoMunicipio: "", nombreMunicipio: "" })}>
          <option value="">—</option>
          {countries.map(([a3, name]) => (
            <option key={a3} value={a3}>
              {name}
            </option>
          ))}
        </select>
      </label>
      <label>
        <Label bad={has("municipio")}>{spain ? t("ses.municipality") : t("ses.city")}</Label>
        {spain ? (
          <MunicipioPicker
            list={municipios}
            value={value.codigoMunicipio ? { code: value.codigoMunicipio, name: value.municipioNombre } : null}
            bad={has("municipio")}
            placeholder={t("ses.municipality_placeholder")}
            onPick={(m) => onChange({ ...value, codigoMunicipio: m?.code || "", municipioNombre: m?.name || "" })}
          />
        ) : (
          <input className={`${inputCls} ${has("municipio") ? badCls : ""}`} value={value.nombreMunicipio || ""} onChange={(e) => set("nombreMunicipio", e.target.value)} maxLength={100} />
        )}
      </label>
      <label>
        <Label bad={has("codigoPostal")}>{t("ses.postcode")}</Label>
        <input className={`${inputCls} ${has("codigoPostal") ? badCls : ""}`} value={value.codigoPostal || ""} onChange={(e) => set("codigoPostal", e.target.value)} maxLength={20} />
      </label>
      <label>
        <Label bad={has("contacto")}>{t("ses.phone")}</Label>
        <input className={`${inputCls} ${has("contacto") ? badCls : ""}`} value={value.telefono || ""} onChange={(e) => set("telefono", e.target.value)} maxLength={20} inputMode="tel" />
      </label>
      <label className="sm:col-span-2">
        <Label bad={has("contacto") || has("correo")} hint={t("ses.contact_rule")}>{t("ses.email")}</Label>
        <input className={`${inputCls} ${has("contacto") || has("correo") ? badCls : ""}`} value={value.correo || ""} onChange={(e) => set("correo", e.target.value)} maxLength={250} inputMode="email" />
      </label>
    </div>
  );
}

export default function SesModal({ entries, onClose }) {
  const { t, i18n } = useTranslation();
  const [tables, setTables] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const now = useMemo(() => new Date(), []);
  const [estab, setEstab] = useState(readStore);
  const [contrato, setContrato] = useState(() => {
    // Entrada a la hora en punto actual; salida, 24 h después (el anfitrión lo ajusta).
    const entrada = new Date(now);
    entrada.setMinutes(0, 0, 0);
    const salida = new Date(entrada.getTime() + 24 * 3600 * 1000);
    return {
      referencia: `WS-${localDate(now).replace(/-/g, "")}-${pad(now.getHours())}${pad(now.getMinutes())}`,
      fechaContrato: localDate(now),
      fechaEntrada: localDateTime(entrada),
      fechaSalida: localDateTime(salida),
      tipoPago: "",
    };
  });
  const [shared, setShared] = useState(entries.length > 1);
  const [addr, setAddr] = useState({ pais: "ESP" });
  const [addrByGuest, setAddrByGuest] = useState({});
  const [overrides, setOverrides] = useState({});
  const [showErrors, setShowErrors] = useState(false);

  useEffect(() => {
    let alive = true;
    Promise.all([import("../data/ses/municipios.json"), import("../data/ses/paises.json"), import("../data/ses/codigos.json")])
      .then(([m, p, c]) => alive && setTables({ municipios: m.default, paises: p.default, codigos: c.default }))
      .catch(() => alive && setLoadError(true));
    return () => {
      alive = false;
    };
  }, []);

  const countries = useMemo(() => {
    if (!tables) return [];
    let dn = null;
    try {
      dn = new Intl.DisplayNames([i18n.language || "es"], { type: "region" });
    } catch (_) {
      dn = null;
    }
    return tables.paises
      .map(([a3, a2]) => [a3, (dn && dn.of(a2 === "UK" ? "GB" : a2)) || a3])
      .sort((a, b) => a[1].localeCompare(b[1], i18n.language || "es"));
  }, [tables, i18n.language]);

  const ids = entries.map((e) => e.resultId || e.requestId);
  const setOv = (id, k, v) => setOverrides((p) => ({ ...p, [id]: { ...(p[id] || {}), [k]: v } }));

  const personas = useMemo(
    () =>
      entries.map((e, i) => {
        const id = ids[i];
        const person = personFromDocument(fieldsOf(e), overrides[id] || {}, contrato.fechaEntrada);
        const a = shared ? addr : addrByGuest[id] || { pais: "ESP" };
        return {
          ...person,
          id,
          direccion: {
            direccion: a.direccion,
            direccionComplementaria: a.direccionComplementaria,
            codigoMunicipio: a.codigoMunicipio,
            nombreMunicipio: a.nombreMunicipio,
            codigoPostal: a.codigoPostal,
            pais: a.pais,
          },
          telefono: a.telefono,
          correo: a.correo,
          parentesco: (overrides[id] || {}).parentesco || "",
        };
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [entries, overrides, contrato.fechaEntrada, shared, addr, addrByGuest]
  );

  const validation = useMemo(() => validateSes({ codigoEstablecimiento: estab, contrato, personas }), [estab, contrato, personas]);
  const errOf = (i) => (showErrors ? validation.guests[i] : validation.guests[i].filter((k) => !["direccion", "codigoPostal", "pais", "municipio", "contacto", "correo"].includes(k)));
  const cErr = (k) => showErrors && validation.contract.includes(k);

  const download = () => {
    if (!validation.ok) {
      setShowErrors(true);
      return;
    }
    try {
      window.localStorage.setItem(STORE_KEY, estab.trim());
    } catch (_) {
      /* sin almacenamiento: no pasa nada */
    }
    const xml = buildSesXml({ codigoEstablecimiento: estab, contrato, personas });
    const blob = new Blob([xml], { type: "application/xml;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `parte_viajeros_${contrato.fechaEntrada.slice(0, 10).replace(/-/g, "")}.xml`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const hasMinor = personas.some((p) => p.menor);
  const adults = personas.filter((p) => !p.menor);

  return (
    <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center bg-black/40 p-2 sm:p-4 overflow-y-auto">
      <div className="w-full max-w-3xl rounded-2xl bg-white shadow-2xl my-4">
        <div className="flex items-start justify-between gap-3 border-b border-gray-100 p-4 sm:p-5">
          <div>
            <h3 className="text-lg font-bold text-gray-900">{t("ses.title")}</h3>
            <p className="mt-1 text-sm text-gray-500">{t("ses.subtitle", { count: entries.length })}</p>
          </div>
          <button onClick={onClose} className="rounded-lg p-1 text-gray-400 hover:bg-gray-100" aria-label={t("ses.close")}>
            <X className="h-5 w-5" />
          </button>
        </div>

        {loadError && <p className="p-5 text-sm text-red-600">{t("ses.load_error")}</p>}
        {!tables && !loadError && <p className="p-5 text-sm text-gray-500">{t("ses.loading")}</p>}

        {tables && (
          <div className="space-y-6 p-4 sm:p-5">
            {entries.length > MAX_GUESTS && <p className="text-sm text-red-600">{t("ses.too_many", { max: MAX_GUESTS })}</p>}

            <section>
              <h4 className="mb-3 text-sm font-bold text-gray-900">{t("ses.stay")}</h4>
              <div className="grid gap-3 sm:grid-cols-2">
                <label>
                  <Label bad={cErr("codigoEstablecimiento")} hint={t("ses.estab_hint")}>{t("ses.estab")}</Label>
                  <input className={`${inputCls} ${cErr("codigoEstablecimiento") ? badCls : ""}`} value={estab} maxLength={10} onChange={(e) => setEstab(e.target.value)} />
                </label>
                <label>
                  <Label bad={cErr("referencia")}>{t("ses.reference")}</Label>
                  <input className={`${inputCls} ${cErr("referencia") ? badCls : ""}`} value={contrato.referencia} maxLength={50} onChange={(e) => setContrato({ ...contrato, referencia: e.target.value })} />
                </label>
                <label>
                  <Label bad={cErr("fechaEntrada")}>{t("ses.checkin")}</Label>
                  <input type="datetime-local" className={`${inputCls} ${cErr("fechaEntrada") ? badCls : ""}`} value={contrato.fechaEntrada} onChange={(e) => setContrato({ ...contrato, fechaEntrada: e.target.value })} />
                </label>
                <label>
                  <Label bad={cErr("fechaSalida")}>{t("ses.checkout")}</Label>
                  <input type="datetime-local" className={`${inputCls} ${cErr("fechaSalida") ? badCls : ""}`} value={contrato.fechaSalida} onChange={(e) => setContrato({ ...contrato, fechaSalida: e.target.value })} />
                </label>
                <label>
                  <Label bad={cErr("fechaContrato")} hint={t("ses.contract_date_hint")}>{t("ses.contract_date")}</Label>
                  <input type="date" className={`${inputCls} ${cErr("fechaContrato") ? badCls : ""}`} value={contrato.fechaContrato} onChange={(e) => setContrato({ ...contrato, fechaContrato: e.target.value })} />
                </label>
                <label>
                  <Label bad={cErr("tipoPago")}>{t("ses.payment")}</Label>
                  <select className={`${inputCls} ${cErr("tipoPago") ? badCls : ""}`} value={contrato.tipoPago} onChange={(e) => setContrato({ ...contrato, tipoPago: e.target.value })}>
                    <option value="">{t("ses.choose")}</option>
                    {tables.codigos.pagos.map(([code]) => (
                      <option key={code} value={code}>
                        {t(`ses.pay.${code}`)}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            </section>

            <section>
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                <h4 className="text-sm font-bold text-gray-900">{t("ses.home")}</h4>
                {entries.length > 1 && (
                  <label className="flex items-center gap-2 text-xs text-gray-600">
                    <input type="checkbox" checked={shared} onChange={(e) => setShared(e.target.checked)} />
                    {t("ses.same_home")}
                  </label>
                )}
              </div>
              <p className="mb-3 text-xs text-gray-500">{t("ses.home_rule")}</p>
              {shared ? (
                <AddressForm value={addr} onChange={setAddr} errors={showErrors ? validation.guests[0] || [] : []} t={t} countries={countries} municipios={tables.municipios} />
              ) : (
                <div className="space-y-4">
                  {personas.map((p, i) => (
                    <div key={p.id} className="rounded-xl border border-gray-200 p-3">
                      <p className="mb-2 text-sm font-semibold text-gray-800">{[p.nombre, p.apellido1].filter(Boolean).join(" ") || t("ses.no_name")}</p>
                      <AddressForm
                        value={addrByGuest[p.id] || { pais: "ESP" }}
                        onChange={(v) => setAddrByGuest((prev) => ({ ...prev, [p.id]: v }))}
                        errors={showErrors ? validation.guests[i] : []}
                        t={t}
                        countries={countries}
                        municipios={tables.municipios}
                      />
                    </div>
                  ))}
                </div>
              )}
            </section>

            <section>
              <h4 className="mb-3 text-sm font-bold text-gray-900">{t("ses.guests")}</h4>
              <ul className="space-y-3">
                {personas.map((p, i) => {
                  const errs = errOf(i);
                  const personErrs = errs.filter((k) => ["nombre", "apellido1", "apellido2", "fechaNacimiento", "tipoDocumento", "numeroDocumento", "soporteDocumento", "parentesco"].includes(k));
                  const ov = overrides[p.id] || {};
                  return (
                    <li key={p.id} className={`rounded-xl border p-3 ${personErrs.length ? "border-amber-300 bg-amber-50/40" : "border-gray-200"}`}>
                      <div className="flex flex-wrap items-center gap-2">
                        {personErrs.length ? <AlertTriangle className="h-4 w-4 text-amber-600" /> : <CheckCircle2 className="h-4 w-4 text-primary-600" />}
                        <span className="font-semibold text-gray-900">{[p.nombre, p.apellido1, p.apellido2].filter(Boolean).join(" ") || t("ses.no_name")}</span>
                        {p.menor && <span className="rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-bold text-blue-700">{t("ses.minor")}</span>}
                      </div>
                      <p className="mt-1 text-xs text-gray-500">
                        {[p.tipoDocumento && p.numeroDocumento ? `${p.tipoDocumento} ${p.numeroDocumento}` : null, p.fechaNacimiento, p.nacionalidad, p.sexo ? t(`ses.sex_${p.sexo}`) : null].filter(Boolean).join(" · ")}
                      </p>
                      {(personErrs.length > 0 || p.tipoDocumento === "NIF") && (
                        <div className="mt-2 grid gap-2 sm:grid-cols-2">
                          {["nombre", "apellido1", "apellido2", "numeroDocumento", "soporteDocumento", "fechaNacimiento"]
                            .filter((k) => personErrs.includes(k) || (p.tipoDocumento === "NIF" && ["apellido1", "apellido2"].includes(k)))
                            .map((k) => (
                              <label key={k}>
                                <Label bad={personErrs.includes(k)} hint={k === "soporteDocumento" ? t("ses.support_hint") : k === "fechaNacimiento" ? "AAAA-MM-DD" : undefined}>
                                  {t(`ses.f_${k}`)}
                                </Label>
                                <input
                                  className={`${inputCls} ${personErrs.includes(k) ? badCls : ""}`}
                                  value={ov[k] ?? p[k] ?? ""}
                                  onChange={(e) => setOv(p.id, k, e.target.value)}
                                />
                              </label>
                            ))}
                          {personErrs.includes("tipoDocumento") && (
                            <label>
                              <Label bad>{t("ses.f_tipoDocumento")}</Label>
                              <select className={`${inputCls} ${badCls}`} value={ov.tipoDocumento || ""} onChange={(e) => setOv(p.id, "tipoDocumento", e.target.value)}>
                                <option value="">{t("ses.choose")}</option>
                                {["NIF", "NIE", "PAS", "OTRO"].map((c) => (
                                  <option key={c} value={c}>
                                    {c}
                                  </option>
                                ))}
                              </select>
                            </label>
                          )}
                        </div>
                      )}
                      {p.menor && hasMinor && (
                        <label className="mt-2 block sm:w-1/2">
                          <Label bad={personErrs.includes("parentesco")} hint={t("ses.relationship_hint")}>
                            {t("ses.relationship")}
                          </Label>
                          <select className={`${inputCls} ${personErrs.includes("parentesco") ? badCls : ""}`} value={ov.parentesco || ""} onChange={(e) => setOv(p.id, "parentesco", e.target.value)}>
                            <option value="">{t("ses.choose")}</option>
                            {tables.codigos.parentescos.map(([code]) => (
                              <option key={code} value={code}>
                                {t(`ses.rel.${code}`)}
                              </option>
                            ))}
                          </select>
                        </label>
                      )}
                    </li>
                  );
                })}
              </ul>
              {hasMinor && adults.length === 0 && <p className="mt-2 text-xs text-amber-700">{t("ses.no_adult")}</p>}
            </section>

            <div className="flex flex-col gap-3 border-t border-gray-100 pt-4 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-xs text-gray-500">{t("ses.how_to_upload")}</p>
              <button
                onClick={download}
                disabled={entries.length === 0 || entries.length > MAX_GUESTS}
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-primary-600 px-5 py-2.5 text-sm font-bold text-white transition hover:bg-primary-700 disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-400"
              >
                <Download className="h-4 w-4" />
                {t("ses.download")}
              </button>
            </div>
            {showErrors && !validation.ok && <p className="text-sm text-amber-700">{t("ses.fix_first")}</p>}
          </div>
        )}
      </div>
    </div>
  );
}
